import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import {
  Typography,
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  MenuItem,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  AccountBalanceWallet as CreditIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import api, { enhancedApi } from '../api';

const SERVICE_OPTIONS = ['W-2', 'Consulting', '1099 Preparation', 'Tax Return', 'Advisory', 'Other'];

const CreateCredit = () => {
  const navigate = useNavigate();
  const { userPermissions } = useContext(AuthContext) || {};
  const [clients, setClients] = useState([]);
  const generateCreditNumber = () => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `CR${yy}${mm}${dd}${hh}${min}${ss}`;
  };

  const [formData, setFormData] = useState({
    client: '',
    date: new Date().toISOString().split('T')[0],
    creditNumber: generateCreditNumber(),
    description: '',
    items: [{ service: '', description: '', quantity: 1, rate: 0, tax: 0, total: 0 }]
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userPermissions?.can_access_credits) {
      setError('No access to credits features.');
      setLoading(false);
      return;
    }

    const fetchClients = async () => {
      try {
        setLoading(true);
        const response = await enhancedApi.getClients();
        const data = response.data;
        setClients(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching clients:', err);
        setError('Failed to load clients');
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, [userPermissions]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleItemChange = (index, field, value) => {
    const numVal = ['quantity', 'rate', 'tax'].includes(field) ? parseFloat(value) || 0 : value;
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: numVal };
    newItems[index].total = newItems[index].quantity * newItems[index].rate * (1 + newItems[index].tax / 100);
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { service: '', description: '', quantity: 1, rate: 0, tax: 0, total: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) return;
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  const subtotal = formData.items.reduce((sum, item) => sum + item.total, 0);
  const totalTax = formData.items.reduce((sum, item) => sum + (item.total * (item.tax || 0) / 100), 0);
  const grandTotal = subtotal + totalTax;

  const saveCredit = async () => {
    if (!formData.client) {
      setError('Client is required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const creditNumber = formData.creditNumber?.trim() || generateCreditNumber();
      await api.post('/api/billing/credits/create/', {
        client: formData.client,
        date: formData.date,
        credit_number: creditNumber,
        description: formData.description,
        items: formData.items
      });
      navigate('/billing/credits');
    } catch (err) {
      console.error('Error saving credit:', err);
      setError(err.response?.data?.detail || 'Failed to save credit');
    } finally {
      setSaving(false);
    }
  };

  if (error && !userPermissions?.can_access_credits) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/billing/credits')}
            sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}
          >
            Back to Credits
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CreditIcon sx={{ fontSize: 36, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                Create Credit
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add a new credit memo for a client
              </Typography>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box component="form">
          {/* Section: Client & Details */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon fontSize="small" /> Credit Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Client"
                  name="client"
                  value={formData.client}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  size="small"
                  helperText={clients.length === 0 ? 'No clients found. Add clients first.' : null}
                  disabled={clients.length === 0}
                >
                  <MenuItem value="">Select client</MenuItem>
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.display_name || client.name || `Client ${client.id}`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Credit #"
                  name="creditNumber"
                  value={formData.creditNumber}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                  helperText="Auto-generated; edit to override"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Date"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  placeholder="Optional notes about this credit"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Section: Line Items */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Line Items
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addItem}
                sx={{ textTransform: 'none' }}
              >
                Add Line
              </Button>
            </Box>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Service</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: 90 }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: 100 }}>Rate</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: 80 }}>Tax %</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: 100 }} align="right">Total</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          value={item.service}
                          onChange={(e) => handleItemChange(index, 'service', e.target.value)}
                          fullWidth
                          variant="standard"
                          sx={{ minWidth: 120 }}
                        >
                          {SERVICE_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          variant="standard"
                          fullWidth
                          placeholder="Item description"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          inputProps={{ min: 0.01, step: 0.01 }}
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          variant="standard"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          inputProps={{ min: 0, step: 0.01 }}
                          value={item.rate || ''}
                          onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                          variant="standard"
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                          value={item.tax || ''}
                          onChange={(e) => handleItemChange(index, 'tax', e.target.value)}
                          variant="standard"
                          placeholder="0"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          ${(item.total || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => removeItem(index)}
                          disabled={formData.items.length <= 1}
                          sx={{ color: 'text.secondary' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {/* Totals */}
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Box sx={{ display: 'flex', gap: 3, minWidth: 200, justifyContent: 'flex-end' }}>
                <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                <Typography variant="body2" fontWeight={500}>${subtotal.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, minWidth: 200, justifyContent: 'flex-end' }}>
                <Typography variant="body2" color="text.secondary">Tax</Typography>
                <Typography variant="body2" fontWeight={500}>${totalTax.toFixed(2)}</Typography>
              </Box>
              <Divider sx={{ width: '100%', my: 1 }} />
              <Box sx={{ display: 'flex', gap: 3, minWidth: 200, justifyContent: 'flex-end' }}>
                <Typography variant="subtitle1" fontWeight={700}>Total</Typography>
                <Typography variant="subtitle1" fontWeight={700}>${grandTotal.toFixed(2)}</Typography>
              </Box>
            </Box>
          </Paper>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/billing/credits')}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={saveCredit}
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {saving ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  Saving…
                </Box>
              ) : (
                'Save Credit'
              )}
            </Button>
          </Box>
        </Box>
    </Box>
  );
};

export default CreateCredit;
